import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import InstantWinner from './InstantWinner';
import Typography from '@material-ui/core/Typography';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import DownArrow from '../../../images/down_arrow.png';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: '14px',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E7F0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    padding: '14px',
    textAlign: 'left',
  },
  title: {
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  titleWithIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  medalIcon: {
    fontSize: '1.35rem',
    color: '#C9A227',
    flexShrink: 0,
  },
  greenText: {
    color: '#1A1A1A',
    fontWeight: 600,
  },
  stats: {
    display: 'flex',
    alignItems: 'stretch',
    marginBottom: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0E4EB',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  statHalf: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 12px',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
  },
  statHalfLeft: {
    borderRight: '1px solid #E0E4EB',
  },
  statLabelGreen: {
    fontWeight: 700,
    color: '#2E7D32',
    marginRight: '6px',
  },
  statLabelBlue: {
    fontWeight: 700,
    color: '#3B7E3D',
    marginRight: '6px',
  },
  statValue: {
    fontWeight: 400,
    color: '#9E9E9E',
  },
  totalBadge: {
    backgroundColor: '#D4EDDA',
    color: '#155724',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontWeight: 700,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  filters: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '420px',
    margin: '4px auto 20px',
    border: '1px solid #B8D2B9',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '20px',
    marginTop: '4px',
    '& button': {
      flex: 1,
      border: 0,
      backgroundColor: '#FFFFFF',
      color: '#1A1A1A',
      padding: '8px 12px',
      cursor: 'pointer',
      borderRadius: 0,
      fontWeight: 600,
    },
    '& button:first-child': {
      borderRight: '1px solid #B8D2B9',
    },
  },
  selected: {
    color: 'white !important',
    borderColor: '#3B7E3D !important',
    backgroundColor: '#3B7E3D !important',
  },
  numbers: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(62px, 1fr))',
    gap: '6px',
    '& span': {
      padding: '4px 6px',
      borderRadius: '6px',
      fontSize: '12px',
      textAlign: 'center',
    }
  },
  won: {
    backgroundColor: '#D8CCFF',
    color: '#4A2A90',
  },
  available: {
    backgroundColor: '#A9EAA5',
    color: '#155724',
  },
  arrow: {
    marginLeft: '10px',
    verticalAlign: 'sub',
  },
}));

export default function InstantWinners({ campaign }) {
  const classes = useStyles();
  const [searchButton, setSearchButton] = useState('numbers');
  const canShowWinnersTab = !!campaign?.show_winners;

  function switchTo(tab) {
    if (tab === 'winners' && !canShowWinnersTab) return;
    setSearchButton(tab);
  }

  const groupedNumbers = campaign.instant_prizes.reduce((acc, prize) => {
    const value = prize.prize_value;
    if (!acc[value]) {
      acc[value] = [];
    }
    acc[value] = acc[value].concat(prize.prize_ticket_numbers).flat().sort((a, b) => a - b);
    return acc;
  }, {});

  function formatValue(value) {
    if (value >= 1000) {
      return (value / 1000).toFixed(1).replace('.0', '') + ' mil';
    }
    return parseFloat(value).toString();
  }


  function padNumber(number) {
    return number.toString().padStart(campaign.number_of_digits, '0');
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <Typography variant="h6" component="div" className={classes.title}>
          <span className={classes.titleWithIcon}>
            <EmojiEventsIcon className={classes.medalIcon} />
            <span className={classes.greenText}>Prêmios Instantâneos</span>
          </span>
          <span className={classes.totalBadge}>Total: {campaign.instant_prize_tickets || 0}</span>
        </Typography>
        <div className={classes.stats}>
          <div className={`${classes.statHalf} ${classes.statHalfLeft}`}>
            <span className={classes.statLabelGreen}>Disponíveis:</span>
            <span className={classes.statValue}>
              {Math.max((campaign.instant_prize_tickets || 0) - (campaign.instant_winners?.length || 0), 0)}
            </span>
          </div>
          <div className={classes.statHalf}>
            <span className={classes.statLabelBlue}>Sorteados:</span>
            <span className={classes.statValue}>{campaign.instant_winners?.length || 0}</span>
          </div>
        </div>
        <div>
          <div className={classes.filters}>
            <button onClick={() => switchTo('numbers')} className={searchButton == 'numbers' ? classes.selected : null}>Números</button>
            {canShowWinnersTab && (
              <button onClick={() => switchTo('winners')} className={searchButton == 'winners' ? classes.selected : null}>Ganhadores</button>
            )}
          </div>
          {
            searchButton == 'numbers' ?
              <div>
                <span style={{ color: '#3A5D3B', fontSize: '0.82rem' }}>Números premiados:</span>
                <br />
                {Object.keys(groupedNumbers).map((value, index) => (
                  <div key={index}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1A1A1A' }}>Prêmio de <span style={{ color: '#2E7D32' }}>R$ {formatValue(value)}</span><img src={DownArrow} alt="seta para baixo" className={classes.arrow} width="15" height="20" /></Typography>
                    <br />
                    <div className={classes.numbers}>
                      {groupedNumbers[value].map(
                        (number, index) => {
                          return <span key={index} className={campaign.instant_winners.some(winner => winner.number == number) ? classes.won : classes.available}>{padNumber(number)}</span>
                        }
                      )}
                    </div>
                    <br />
                  </div>
                ))}
              </div>
              :
              <div>
                {campaign.instant_winners.map((instant_winner, index) => <InstantWinner key={index} index={index + 2} winner={instant_winner} campaign={campaign} />)}
              </div>
          }
        </div>
      </div>
    </ThemeProvider>
  )
}
